import React from 'react';
import Dialog from '@mui/material/Dialog';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { selectModalIsOpen, selectModalType, selectModalProps } from 'src/old/actions/modal/modalSelectors';
import { hideModal } from 'src/old/actions/modal/modalActions';
import { type ComponentType } from 'react';
import InformationalModal from './InformationalModal';
import CustomModal from './CustomModal';
import SuccessModal from './SuccessModal';
import PromptModal from './PromptModal';
import VideoModal from './VideoModal';
import ErrorModal from './ErrorModal';
import { ContractsConfirmationModal } from './ContractsConfirmationModal';
import { PromptConfirmationModal } from './PromptConfirmationModal';

export const MODAL_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  CUSTOM: 'custom',
  PROMPT: 'prompt',
  VIDEO: 'video',
  ERROR: 'error',
  CONTRACTS_CONFIRMATION: 'contracts_confirmation',
  PROMPT_CONFIRMATION: 'prompt_confirmation'
} as const;

type ModalType = (typeof MODAL_TYPES)[keyof typeof MODAL_TYPES];

const MODALS: Record<ModalType, ComponentType<any>> = {
  [MODAL_TYPES.INFO]: InformationalModal,
  [MODAL_TYPES.CUSTOM]: CustomModal,
  [MODAL_TYPES.SUCCESS]: SuccessModal,
  [MODAL_TYPES.PROMPT]: PromptModal,
  [MODAL_TYPES.VIDEO]: VideoModal,
  [MODAL_TYPES.ERROR]: ErrorModal,
  [MODAL_TYPES.CONTRACTS_CONFIRMATION]: ContractsConfirmationModal,
  [MODAL_TYPES.PROMPT_CONFIRMATION]: PromptConfirmationModal
};

type StyleProps = {
  video: boolean;
  noPadding: boolean;
};

const useStyles = makeStyles(theme => ({
  dialogWrapper: {
    fontFamily: theme.typography.fontFamily,
    minWidth: ({ video }: StyleProps) => (video ? 720 : 400),
    minHeight: ({ video }: StyleProps) => (video ? 360 : 200),
    padding: ({ noPadding }: StyleProps) => (noPadding ? 0 : theme.spacing(4)),
    textAlign: 'center',
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
      minWidth: 350
    }
  }
}));

const Modal = () => {
  const dispatch = useDispatch();
  const modalIsOpen = useSelector(selectModalIsOpen);
  const modalType = useSelector(selectModalType);
  const modalProps = useSelector(selectModalProps);

  const onModalClose = () => {
    if (modalProps?.onModalCloseCallback) {
      modalProps.onModalCloseCallback();
    }

    hideModal()(dispatch);
  };

  const classes = useStyles({
    video: modalType === MODAL_TYPES.VIDEO,
    noPadding:
      modalProps?.noPadding || modalType === MODAL_TYPES.CONTRACTS_CONFIRMATION || modalType === MODAL_TYPES.VIDEO // Temporary: until we rewrite all modals to .tsx
  });

  const SpecifiedModal = modalType && MODALS[modalType as ModalType];

  return (
    SpecifiedModal && (
      <Dialog
        maxWidth={modalProps?.size || 'md'}
        fullWidth={modalProps?.fullWidth}
        PaperProps={{ classes: { root: classes.dialogWrapper } }}
        open={modalIsOpen}
        onBackdropClick={onModalClose}
      >
        <SpecifiedModal closeModal={onModalClose} {...modalProps} />
      </Dialog>
    )
  );
};

export default Modal;
